import {areSetsEqual} from '@benchling/js-utils';
import React, {useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';

import {assert, assertAndContinue} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import DataConnector from 'connect/DataConnector';
import notifications from 'notifications';
import EditableRequestAssignees from 'requests/assignees/EditableRequestAssignees';
import prepareAssigneesForServer from 'requests/assignees/prepareAssigneesForServer';
import RequestAssignees from 'requests/assignees/RequestAssignees';
import {UpdateRequestArgs} from 'requests/inputs/InputReduxActions';
import {MinimalRequestAssignee, Request, RequestAssignee, RequestSchema} from 'requests/RequestConstants';
import * as AssaySchemaActions from 'results/AssaySchemaActions';
import AssayStore from 'results/AssayStore';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import EditableDateField from 'ui-library/EditableDateField';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import DateField from 'ui-library/inputs/DateField';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LABEL_STYLES} from 'ui-library/inputs/InputConstants';
import Modal from 'ui-library/modals/Modal';
import TextValue from 'ui-library/TextValue';
import {isValidDate} from 'util/DateUtil';

const DIFFERENT_ASSIGNEES_TEXT = 'Requests have different assignees';
const DIFFERENT_SCHEDULED_ON_TEXT = 'Requests are scheduled on different dates';

export enum FieldToUpdate {
  ASSIGNEES = 'ASSIGNEES',
  SCHEDULED_ON = 'SCHEDULED_ON',
}
export interface BulkUpdateRequestsModalBaseProps {
  schemaId: string;
  requests: Array<Request>;
  fieldToUpdate: FieldToUpdate;

  onRequestSubmit: (data: UpdateRequestArgs) => Promise<void>;
  onRequestClose: () => void;
}

class BulkUpdateRequestsModalConnector extends DataConnector<
  BulkUpdateRequestsModalBaseProps,
  BulkUpdateRequestsModalConnectedProps
> {
  stores = [AssayStore];

  enhanceProps(): BulkUpdateRequestsModalConnectedProps {
    const storeSchema = AssayStore.getRequestSchema(this.props.schemaId);
    return {schema: storeSchema};
  }
}

export interface BulkUpdateRequestsModalConnectedProps {
  schema: RequestSchema | null;
}

/* A modal for bulk updating multiple requests of the provided request schemaId. */
export function BulkUpdateRequestsModal(
  props: BulkUpdateRequestsModalBaseProps & BulkUpdateRequestsModalConnectedProps
): JSX.Element {
  const [updateInProcess, setUpdateInProcess] = useStateIfMounted(false);

  const [newAssignees, setNewAssignees] = useState<Array<MinimalRequestAssignee>>();
  useOnMount(() => {
    AssaySchemaActions.fetchSchemaSpec(props.schemaId);
  });
  const [newScheduledOn, setNewScheduledOn] = useState<string>();

  assert(props.requests.length > 0, 'BulkUpdateRequestsModal opened with no requests');

  async function _handleUpdateRequest(): Promise<void> {
    setUpdateInProcess(true);

    const updateRequestArgs: UpdateRequestArgs = {};

    if (newAssignees != null) {
      assertAndContinue(
        props.fieldToUpdate === FieldToUpdate.ASSIGNEES,
        `Changed assignees when fieldToUpdate is ${props.fieldToUpdate}`
      );
      updateRequestArgs.assignees = prepareAssigneesForServer(newAssignees);
    }

    if (newScheduledOn != null) {
      assertAndContinue(
        props.fieldToUpdate === FieldToUpdate.SCHEDULED_ON,
        `Changed scheduled on when fieldToUpdate is ${props.fieldToUpdate}`
      );
      if (isValidDate({textValue: newScheduledOn})) {
        updateRequestArgs.scheduledOn = newScheduledOn;
      } else {
        notifications.error('Invalid format for scheduled on date.');
        setUpdateInProcess(false);
        return;
      }
    }

    // Just close the modal if no changes were made.
    if (isEqual(updateRequestArgs, {})) {
      props.onRequestClose();
      return;
    }

    try {
      await props.onRequestSubmit(updateRequestArgs);
      props.onRequestClose();
    } catch (err) {
      // A toast has already been shown.
      setUpdateInProcess(false);
      throw err;
    }
  }

  function _requestsHaveTheSameAssignees(): boolean {
    const assigneeSet = new Set(props.requests[0].assignees);
    for (const request of props.requests) {
      if (!areSetsEqual(assigneeSet, new Set(request.assignees), _areAssigneesEqual)) {
        return false;
      }
    }
    return true;
  }

  function _areAssigneesEqual(assignee1: RequestAssignee, assignee2: RequestAssignee): boolean {
    if (assignee1.user) {
      if (assignee2.user?.id === assignee1.user.id) {
        return true;
      }
    }
    if (assignee1.team) {
      if (assignee2.team?.id === assignee1.team.id) {
        return true;
      }
    }
    return false;
  }

  function _requestsHaveTheSameScheduledOn(): boolean {
    const firstScheduledOn = props.requests[0].scheduledOn;
    return props.requests.every((req) => req.scheduledOn === firstScheduledOn);
  }

  const requestsHaveTheSameAssignees = _requestsHaveTheSameAssignees();
  const requestsHaveTheSameScheduledOn = _requestsHaveTheSameScheduledOn();
  // We need to memoize the initial assignee because computing it inline would recreate a new object on every
  // render if requestsHaveTheSameAssignees is false which would trigger changes in the EditableRequestAssignee
  // and downstream components.
  const baseAssignee = useMemo(
    () => (requestsHaveTheSameAssignees ? props.requests[0].assignees : []),
    [requestsHaveTheSameAssignees, props.requests]
  );

  if (!props.schema) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <Modal
      large={true}
      closeableOnEscapeKey={false}
      scrollable={false}
      expandHeight={false}
      title={`Update ${props.schema.name} ${pluralize(props.requests.length, 'Request')}`}
      buttons={[
        {
          text: updateInProcess ? 'Updating...' : 'Update',
          onClick: _handleUpdateRequest,
          style: ButtonStyle.PRIMARY,
          disabled: updateInProcess,
        },
      ]}
      onRequestClose={props.onRequestClose}
    >
      <div>
        {props.fieldToUpdate === FieldToUpdate.ASSIGNEES && (
          <Row>
            <Col xs={6}>
              <FieldLabel label="Previously assigned to:" labelStyle={FieldLabel.STYLES.LABEL} />
              <div className="u-tiny-spacing-top">
                {requestsHaveTheSameAssignees ? (
                  <RequestAssignees assignees={props.requests[0].assignees} />
                ) : (
                  <TextValue value={DIFFERENT_ASSIGNEES_TEXT} />
                )}
              </div>
            </Col>
            <Col xs={6}>
              <FieldLabel label="New assignee(s):" labelStyle={FieldLabel.STYLES.LABEL} />
              <EditableRequestAssignees
                assignees={newAssignees ?? baseAssignee}
                onUpdateAssignees={setNewAssignees}
                organizationId={props.schema.organization.id}
                shouldChangeBeConfirmed={false}
              />
            </Col>
          </Row>
        )}
        {props.fieldToUpdate === FieldToUpdate.SCHEDULED_ON && (
          <Row>
            <Col xs={6}>
              <FieldLabel label="Previously scheduled on:" labelStyle={FieldLabel.STYLES.LABEL} />
              <div className="u-tiny-spacing-top">
                {requestsHaveTheSameScheduledOn ? (
                  <EditableDateField
                    onChange={() => {}} // onChange is required even though this is disabled
                    value={props.requests[0].scheduledOn}
                    isDisabled={true}
                  />
                ) : (
                  <TextValue value={DIFFERENT_SCHEDULED_ON_TEXT} />
                )}
              </div>
            </Col>
            <Col xs={6}>
              <FieldLabel label="New scheduled on:" labelStyle={FieldLabel.STYLES.LABEL} />
              <DateField
                labelStyle={LABEL_STYLES.HIDDEN}
                onChange={setNewScheduledOn}
                value={
                  newScheduledOn ?? (requestsHaveTheSameScheduledOn ? props.requests[0].scheduledOn : '')
                }
              />
            </Col>
          </Row>
        )}
      </div>
    </Modal>
  );
}

export default BulkUpdateRequestsModalConnector.connect(BulkUpdateRequestsModal);
